import { Container, Input } from 'components/atoms';
import { FormInput } from 'components/molecules';
import VideoPlayer from 'components/organisms/video-player/VideoPlayer';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowsClockwise, PiImage, PiVideoCameraSlash } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { DIGITAL_PRODUCT_TYPE } from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { useGetTrackLightList } from 'services/tracks/light';
import { useGetVideoLightList } from 'services/video/light';
import { getImageSize } from 'utils/components/inputs/image';

const MusicProductDigitalVideo = () => {
  const { t } = useTranslation();

  const {
    values,
    status,
    setFieldValue,
    initialValues,
  }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const videos = useGetVideoLightList();
  const tracksLightList = useGetTrackLightList(
    values.productType === DIGITAL_PRODUCT_TYPE.video,
  );

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const trackSelectOptions = useMemo(
    () =>
      tracksLightList.data?.map(track => ({
        value: track.id,
        label: track.mainTitle,
      })) ?? [],
    [tracksLightList.data],
  );

  const selectedSourceTrack = useMemo(
    () =>
      trackSelectOptions.find(
        option => option.value === values.referredTrackId,
      ),
    [trackSelectOptions, values.referredTrackId],
  );

  useEffect(() => {
    if (values.productType === DIGITAL_PRODUCT_TYPE.audio) {
      navigate(pathname.replace('video', 'tracks'));
    }
  }, [values.productType, undefined]);

  return (
    <div className='flex w-full items-start gap-4'>
      <Container className='w-[900px]'>
        <Container.Header
          title={
            <div className='flex w-full gap-4'>
              <div className='w-[300px]'>
                <FormInput.Select
                  name='videoId'
                  label={t('input.label.video')}
                  value={
                    values.video
                      ? {
                          value: values.video.videoId,
                          label: values.video.title,
                        }
                      : undefined
                  }
                  options={videos.data?.map(v => ({
                    value: v.id,
                    label: v.mainTitle,
                  }))}
                />
              </div>
              <div className='w-[300px]'>
                <FormInput.Select
                  name='referredTrackId'
                  label={t('input.label.sourceTrack')}
                  value={
                    selectedSourceTrack
                      ? {
                          value: selectedSourceTrack.value,
                          label: selectedSourceTrack.label,
                        }
                      : undefined
                  }
                  options={trackSelectOptions}
                  isLoading={tracksLightList.isLoading}
                  onChange={option => {
                    setFieldValue('referredTrackId', option?.value ?? '');
                  }}
                />
              </div>
            </div>
          }
        />
        <Container.Content className='flex flex-col justify-center items-center p-4 rounded-b-md overflow-hidden w-[900px]'>
          {values.video?.videoId ? (
            <VideoPlayer videoId={values.video.videoId} />
          ) : values.video?.originalLink ? (
            <div className='flex flex-col items-center justify-center w-full h-[510px] gap-5'>
              <PiArrowsClockwise
                className='h-[150px] w-[150px] opacity-25 animate-spin'
                style={{ animationDuration: `3000ms` }}
              />
              <p className='opacity-50 text-lg'>
                {t('molecules.sections.converting')}
              </p>
            </div>
          ) : (
            <div className='flex items-center justify-center w-full h-[510px]'>
              <PiVideoCameraSlash className='h-[150px] w-[150px] opacity-25' />
            </div>
          )}
        </Container.Content>
      </Container>
      <Container className='w-[384px]'>
        <Container.Header
          title={
            <>
              <PiImage /> {t('molecules.sections.album-cover-fullhd')}
            </>
          }
        />
        <Container.Content className='h-[216px] '>
          <Input.Image
            fullResolutionUrl={values.thumbnailImageLink ?? undefined}
            editable={!status?.displayMode}
            accept='image/*'
            convertToJpeg
            onChange={file => setFieldValue('thumbnailFile', file)}
            onCancel={action => {
              setFieldValue('thumbnailImageLink', '');
              setFieldValue('thumbnailFile', undefined);
              action.close();
            }}
            onDelete={
              initialValues.thumbnailImageLink
                ? action => {
                    setFieldValue('thumbnailImageLink', '');
                    setFieldValue('thumbnailFile', undefined);
                    action.close();
                  }
                : undefined
            }
            imageSize={getImageSize(1080, 1920)}
          />
        </Container.Content>
      </Container>
    </div>
  );
};

export default MusicProductDigitalVideo;
